// src/components/client/UpdateRecipeUser.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  Alert,
  Divider,
  Autocomplete,
  Chip
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { userHasActiveEntitlement } from "../../utils/entitlements";
import { FRONTEND_URL, API_BASE_URL } from "../../config/urls";

// ---------- helpers ----------
const normalizeLang = (l) => {
  if (!l) return "en";
  if (l === "gr") return "el";
  return l;
};

const pickLocalized = (obj, base, lang) => {
  if (!obj) return "";
  const l = normalizeLang(lang);
  return obj?.[`${base}_${l}`] || obj?.[`${base}_en`] || obj?.[`${base}_el`] || obj?.[base] || "";
};

const toIdArray = (maybeIds, maybeObjs) => {
  if (Array.isArray(maybeIds) && maybeIds.length && typeof maybeIds[0] !== "object") {
    return maybeIds.map((x) => Number(x)).filter((n) => Number.isFinite(n));
  }
  if (Array.isArray(maybeObjs) && maybeObjs.length) {
    return maybeObjs.map((x) => Number(x?.id)).filter((n) => Number.isFinite(n));
  }
  return [];
};

const normalizeProducts = (arr) => {
  if (!Array.isArray(arr)) return [{ generic_product_id: "", quantity: "" }];
  const out = arr.map((p) => ({
    generic_product_id: p?.generic_product_id ? String(p.generic_product_id) : "",
    quantity: p?.quantity ?? ""
  }));
  return out.length ? out : [{ generic_product_id: "", quantity: "" }];
};

const asArray = (x) => (Array.isArray(x) ? x : []);

// ✅ Χωρίζουμε BASE για uploads (Plesk) και backend (Flask)
const UPLOAD_BASE = (process.env.REACT_APP_UPLOAD_API_BASE || FRONTEND_URL).replace(/\/$/, "");
const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || API_BASE_URL.replace(/\/api$/, "")).replace(/\/$/, "");

// ✅ Ενιαίο resolver: /uploads => UPLOAD_BASE, /static => BACKEND_BASE
const getFullImageUrl = (url) => {
  if (!url) return "";

  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  if (url.startsWith("/uploads/")) return UPLOAD_BASE + url;
  if (url.startsWith("uploads/")) return UPLOAD_BASE + "/" + url;

  if (url.startsWith("/static/")) return BACKEND_BASE + url;
  if (url.startsWith("static/")) return BACKEND_BASE + "/" + url;

  // παλιά συμβατότητα
  let clean = url;
  if (clean.startsWith("images/")) clean = clean.replace(/^images\//, "");
  if (clean.startsWith("recipes/")) clean = clean.replace(/^recipes\//, "");

  // default uploads path
  return UPLOAD_BASE + "/uploads/recipes/" + clean.replace(/^\/+/, "");
};

// ✅ ΙΔΙΑ λογική με admin: upload σε Plesk PHP ➜ setImageUrl στο backend
async function uploadAndSaveRecipeImageForUser(recipeId, file) {
  // 1) upload to Plesk (PHP)
  const fd = new FormData();
  fd.append("image", file);

  const up = await fetch(`${UPLOAD_BASE}/recipe-image.php`, {
    method: "POST",
    body: fd,
    credentials: "include"
  });

  const upJson = await up.json().catch(() => null);
  if (!up.ok || !upJson?.success) {
    throw new Error(upJson?.error || "Upload to Plesk failed");
  }

  const imageUrl = upJson.image_url; // full url

  // 2) save to backend DB
  const res2 = await api.recipes.setImageUrl(recipeId, imageUrl);
  if (!res2?.data?.success) {
    throw new Error(res2?.data?.error || "DB update failed");
  }

  return imageUrl;
}

const UpdateRecipeUser = () => {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const params = useParams();
  const navigate = useNavigate();

  // route: /:country/client/update-recipe/:id
  const lang = useMemo(
    () => normalizeLang(params.country || i18n.language || "en"),
    [params.country, i18n.language]
  );

  const recipeSlug = params.id;
  const [recipeId, setRecipeId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // lookups
  const [mealTypes, setMealTypes] = useState([]);
  const [foodTypes, setFoodTypes] = useState([]);
  const [dietTypes, setDietTypes] = useState([]);
  const [religiousPrefs, setReligiousPrefs] = useState([]);
  const [countryRecipes, setCountryRecipes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [genericProductList, setGenericProductList] = useState([]);

  // form
  const [form, setForm] = useState({
    name: "",
    ingredients: "",
    instructions: "",
    difficulty: "",
    prep_time: "",
    calories: "",
    country_recipes_id: "",
    season_id: "",
    visibility: "private",
    meal_type_ids: [],
    food_type_ids: [],
    diet_type_ids: [],
    religious_preference_ids: [],
    image_url: "",
    products: [{ generic_product_id: "", quantity: "" }]
  });

  // image
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  // ✅ cleanup objectURL για να μην κάνει leak
  useEffect(() => {
    return () => {
      if (imagePreviewUrl?.startsWith("blob:")) {
        try { URL.revokeObjectURL(imagePreviewUrl); } catch { }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdmin = !!user?.is_admin;
  const isPremium = userHasActiveEntitlement(user);
  const safeProducts = useMemo(
    () => (Array.isArray(form.products) && form.products.length ? form.products : normalizeProducts(form.products)),
    [form.products]
  );

  useEffect(() => {
    if (!Array.isArray(form.products) || form.products.length === 0) {
      setForm((prev) => ({ ...prev, products: normalizeProducts(prev.products) }));
    }
  }, [form.products]);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const loadAll = async () => {
    setLoading(true);
    setError("");
    setInfo("");

    try {
      // 1) Lookups: ΜΗΝ αφήνεις ένα αποτυχημένο request να ρίξει όλο το load
      const results = await Promise.allSettled([
        api.household.getMealTypes(),
        api.household.getFoodTypes(),
        api.household.getCountryRecipes(),
        api.household.getDietTypes(),
        api.household.getSeasons(),
        api.household.getReligiousPreferences(),
        api.genericProducts.list({ params: { lang } })
      ]);

      const [
        mealTypesRes,
        foodTypesRes,
        countryRes,
        dietRes,
        seasonsRes,
        religiousRes,
        genericRes
      ] = results.map((r) => (r.status === "fulfilled" ? r.value : null));

      // Αν κάποιο lookup απέτυχε, βγάλ’ το στο console για να ξέρουμε ποιο είναι
      results.forEach((r, idx) => {
        if (r.status === "rejected") {
          console.error("Lookup failed index:", idx, r.reason?.response?.status, r.reason?.response?.data || r.reason?.message);
        }
      });

      setMealTypes(Array.isArray(mealTypesRes?.data) ? mealTypesRes.data : []);
      setFoodTypes(Array.isArray(foodTypesRes?.data) ? foodTypesRes.data : []);
      setCountryRecipes(Array.isArray(countryRes?.data) ? countryRes.data : []);
      setDietTypes(Array.isArray(dietRes?.data) ? dietRes.data : []);
      setSeasons(Array.isArray(seasonsRes?.data) ? seasonsRes.data : []);

      const relPayload = religiousRes?.data;
      const relItems = Array.isArray(relPayload?.items)
        ? relPayload.items
        : Array.isArray(relPayload)
          ? relPayload
          : [];
      setReligiousPrefs(relItems);

      const gp = genericRes?.data?.data ?? genericRes?.data ?? [];
      setGenericProductList(Array.isArray(gp) ? gp : []);

      // 2) Recipe by slug (εδώ θέλουμε να “σκάσει” αν δεν βρεθεί)
      const safeSlug = encodeURIComponent(recipeSlug || "");
      if (!safeSlug) throw new Error("Missing recipe slug");

      // ⚠️ Εδώ είναι το πιο πιθανό σημείο που σπάει.
      // Κάνε το call όσο πιο “απλό” γίνεται για να ταιριάξει με το api.js σου.
      const recipeRes = await api.recipes.getBySlug(safeSlug, { params: { lang } });
      const r = recipeRes?.data?.data || recipeRes?.data?.recipe || null;

      if (!r) {
        setError(t("recipe_not_found", "Η συνταγή δεν βρέθηκε."));
        setLoading(false);
        return;
      }

      setRecipeId(r.id);

      const mealIds = toIdArray(r.meal_type_ids, r.meal_types);
      const foodIds = toIdArray(r.food_type_ids, r.food_types);
      const dietIds = toIdArray(r.diet_type_ids, r.diet_types);

      const relIds = Array.isArray(r.religious_preference_ids)
        ? r.religious_preference_ids.map(Number).filter((n) => Number.isFinite(n))
        : Array.isArray(r.religious_preferences)
          ? r.religious_preferences.map((x) => Number(x?.id ?? x)).filter((n) => Number.isFinite(n))
          : [];
      const ingredientsText =
        r.ingredients_text ??
        r[`ingredients_${lang}`] ??
        r.ingredients ??
        "";
      setForm({
        name: r.name || "",
        ingredients: typeof ingredientsText === "string" ? ingredientsText : "",
        products: normalizeProducts(r.products || r.ingredients_struct || []),

        instructions: typeof r.instructions === "string" ? r.instructions : (r.instructions || ""),
        difficulty: r.difficulty || "",
        prep_time: r.prep_time ?? "",
        calories: r.calories ?? "",
        country_recipes_id: r.country_recipes_id || "",
        season_id: r.season_id || "",
        visibility: r.visibility || "private",
        meal_type_ids: mealIds,
        food_type_ids: foodIds,
        diet_type_ids: dietIds,
        religious_preference_ids: relIds,
        image_url: r.image_url || "",
      });

      setImagePreviewUrl(getFullImageUrl(r.image_url || ""));
      setImageFile(null);

      setLoading(false);
    } catch (e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.error || e?.response?.data?.message;
      console.error("UpdateRecipeUser loadAll error:", status, e?.response?.data || e?.message);

      // ✅ Δείξε το πραγματικό πρόβλημα στον UI
      setError(serverMsg || (status ? `Load failed (${status})` : t("load_failed", "Αποτυχία φόρτωσης δεδομένων.")));
      setLoading(false);
    }
  };


  useEffect(() => {
    if (!recipeSlug) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeSlug, lang]);


  // ✅ ΙΔΙΟ FLOW με admin:
  // - upload Plesk
  // - setImageUrl στη DB
  // - ενημέρωση state + preview
  const uploadImageIfNeeded = async () => {
    if (!imageFile) return form.image_url || "";

    const newUrl = await uploadAndSaveRecipeImageForUser(recipeId, imageFile);

    setForm((prev) => ({ ...prev, image_url: newUrl }));
    setImagePreviewUrl(getFullImageUrl(newUrl));

    return newUrl;
  };

  const sanitizeProductsForPayload = () => {
    return (form.products || [])
      .filter((p) => p.generic_product_id && String(p.quantity).trim() !== "")
      .map((p) => ({
        generic_product_id: Number(p.generic_product_id),
        quantity: Number(p.quantity)
      }));
  };

  const buildPayload = async (overrideVisibility = null) => {
    const image_url = await uploadImageIfNeeded();

    const desiredVisibility = isAdmin
      ? (overrideVisibility ?? form.visibility ?? "private")
      : isPremium
        ? (overrideVisibility ?? form.visibility ?? "private")
        : "private";

    return {
      [`name_${lang}`]: form.name,
      [`ingredients_${lang}`]: form.ingredients,
      [`instructions_${lang}`]: form.instructions,

      country_recipes_id: form.country_recipes_id,
      season_id: form.season_id,

      difficulty: form.difficulty || null,
      prep_time: form.prep_time === "" ? null : Number(form.prep_time),
      calories: form.calories === "" ? null : Number(form.calories),

      image_url,
      visibility: desiredVisibility,

      meal_type_ids: form.meal_type_ids,
      food_type_ids: form.food_type_ids,
      diet_type_ids: form.diet_type_ids,
      religious_preference_ids: form.religious_preference_ids,

      products: sanitizeProductsForPayload()
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setInfo("");

    try {
      const payload = await buildPayload();

      // update recipe data
      await api.recipes.update(recipeId, payload, lang);

      setImageFile(null);
      navigate(`/${lang}/client/recipes-user`);
    } catch (e) {
      console.error("UpdateRecipeUser save error:", e?.response?.data || e.message);
      setError(e?.response?.data?.error || t("save_failed", "Αποτυχία αποθήκευσης."));
    } finally {
      setSaving(false);
    }
  };

  const handleRequestPublic = async () => {
    setSaving(true);
    setError("");
    setInfo("");

    try {
      const payload = await buildPayload("public");
      await api.recipes.update(recipeId, payload, lang);

      setImageFile(null);

      setInfo(
        t(
          "public_request_sent",
          "Το αίτημα για δημοσίευση στάλθηκε. Η συνταγή θα ελεγχθεί και θα γίνει δημόσια μετά την έγκριση."
        )
      );

      await loadAll();
    } catch (e) {
      console.error("UpdateRecipeUser request public error:", e?.response?.data || e.message);
      setError(e?.response?.data?.error || t("save_failed", "Αποτυχία αποθήκευσης."));
    } finally {
      setSaving(false);
    }
  };

  const getProductLabel = (option) => {
    if (!option) return "";

    // Αν έχει multilingual JSON
    if (typeof option.name === "object" && option.name !== null) {
      return option.name[lang] || option.name.en || "";
    }

    // Αν έχει name_el / name_en
    if (option.name_el || option.name_en) {
      return option.name_el || option.name_en;
    }

    // fallback
    return String(option.name || option.id || "");
  };


  if (authLoading || loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("login_required", "Πρέπει να συνδεθείτε για να επεξεργαστείτε συνταγές.")}
        </Alert>
        <Button variant="contained" onClick={() => navigate(`/${lang}/login`)}>
          {t("login", "Σύνδεση")}
        </Button>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, pb: 6 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Typography variant="h4">
          {t("update_recipe_title", "Ενημέρωση συνταγής")}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(`/${lang}/client/recipes-user`)}>
          {t("back", "Πίσω")}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

      {/* IMAGE */}
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" component="label">
          {t("upload_image", "Μεταφόρτωση φωτογραφίας")}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setImageFile(f);

              // local preview πριν το save
              if (f) {
                const objUrl = URL.createObjectURL(f);
                setImagePreviewUrl(objUrl);
              } else {
                setImagePreviewUrl(getFullImageUrl(form.image_url || ""));
              }
            }}
          />
        </Button>
      </Box>

      {imagePreviewUrl && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 520,
            aspectRatio: "3 / 2",
            overflow: "hidden",
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            mb: 3
          }}
        >
          <img
            src={imagePreviewUrl}
            alt="preview"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      )}

      {/* BASIC */}
      <TextField
        label={t("recipe_name", "Όνομα")}
        value={form.name}
        onChange={handleChange("name")}
        fullWidth
        sx={{ mb: 2 }}
      />

      <TextField
        label={t("ingredients", "Υλικά")}
        value={form.ingredients}
        onChange={handleChange("ingredients")}
        fullWidth
        multiline
        minRows={4}
        sx={{ mb: 2 }}
      />

      <TextField
        label={t("instructions", "Οδηγίες")}
        value={form.instructions}
        onChange={handleChange("instructions")}
        fullWidth
        multiline
        minRows={5}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 3 }} />

      {/* Structured products */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("ingredients_structured", "Υλικά (δομημένα)")}
      </Typography>

      {safeProducts.map((p, idx) => (
        <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 260 }}
            options={asArray(genericProductList)}
            getOptionLabel={getProductLabel}

            value={
              asArray(genericProductList).find(
                (gp) => String(gp?.id) === String(p.generic_product_id)
              ) || null
            }
            onChange={(_, newVal) => {
              setForm((prev) => {
                const prevProducts = Array.isArray(prev.products) ? prev.products : normalizeProducts(prev.products);
                return {
                  ...prev,
                  products: prevProducts.map((row, i) =>
                    i === idx ? { ...row, generic_product_id: newVal?.id ? String(newVal.id) : "" } : row
                  ),
                };
              });
            }}
            renderInput={(params2) => (
              <TextField {...params2} label={t("generic_product", "Γενικό προϊόν")} />
            )}
          />

          <TextField
            sx={{ width: 160 }}
            label={t("quantity_g", "Ποσότητα (g/ml)")}
            type="number"
            value={p.quantity}
            onChange={(e) => {
              const v = e.target.value;
              setForm((prev) => {
                const prevProducts = Array.isArray(prev.products) ? prev.products : normalizeProducts(prev.products);
                return {
                  ...prev,
                  products: prevProducts.map((row, i) =>
                    i === idx ? { ...row, quantity: v } : row
                  ),
                };
              });
            }}
          />

          <Button
            variant="outlined"
            onClick={() => {
              setForm((prev) => {
                const prevProducts = Array.isArray(prev.products) ? prev.products : normalizeProducts(prev.products);
                const next = prevProducts.filter((_, i) => i !== idx);
                return { ...prev, products: next.length ? next : [{ generic_product_id: "", quantity: "" }] };
              });
            }}
          >
            {t("remove", "Αφαίρεση")}
          </Button>
        </Box>
      ))}

      <Button
        variant="outlined"
        onClick={() => {
          setForm((prev) => ({
            ...prev,
            products: [...(prev.products || []), { generic_product_id: "", quantity: "" }]
          }));
        }}
        sx={{ mb: 2 }}
      >
        {t("add_ingredient", "Προσθήκη υλικού")}
      </Button>

      <Divider sx={{ my: 3 }} />

      {/* META */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mb: 2 }}>
        <TextField
          select
          label={t("difficulty.label", "Δυσκολία")}
          value={form.difficulty}
          onChange={handleChange("difficulty")}
          fullWidth
        >
          <MenuItem value="easy">{t("easy", "Εύκολη")}</MenuItem>
          <MenuItem value="medium">{t("medium", "Μέτρια")}</MenuItem>
          <MenuItem value="hard">{t("hard", "Δύσκολη")}</MenuItem>
        </TextField>

        <TextField
          label={t("prep_time.label", "Χρόνος (λεπτά)")}
          type="number"
          value={form.prep_time}
          onChange={handleChange("prep_time")}
          fullWidth
        />

        <TextField
          label={t("calories.label", "Θερμίδες")}
          type="number"
          value={form.calories}
          onChange={handleChange("calories")}
          fullWidth
        />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 2 }}>
        <TextField
          select
          label={t("country", "Χώρα")}
          value={form.country_recipes_id}
          onChange={handleChange("country_recipes_id")}
          fullWidth
        >
          {countryRecipes.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {pickLocalized(c, "country_name", lang)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label={t("season", "Εποχή")}
          value={form.season_id}
          onChange={handleChange("season_id")}
          fullWidth
        >
          {seasons.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {pickLocalized(s, "name", lang)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* TYPES */}
      <Autocomplete
        multiple
        options={mealTypes}
        value={mealTypes.filter((x) => form.meal_type_ids.includes(x.id))}
        onChange={(_, vals) => setForm((p) => ({ ...p, meal_type_ids: vals.map((v) => v.id) }))}
        getOptionLabel={(o) => pickLocalized(o, "name", lang)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip label={pickLocalized(option, "name", lang)} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params2) => <TextField {...params2} label={t("meal_types", "Γεύματα")} sx={{ mb: 2 }} />}
      />

      <Autocomplete
        multiple
        options={foodTypes}
        value={foodTypes.filter((x) => form.food_type_ids.includes(x.id))}
        onChange={(_, vals) => setForm((p) => ({ ...p, food_type_ids: vals.map((v) => v.id) }))}
        getOptionLabel={(o) => pickLocalized(o, "type_name", lang)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip label={pickLocalized(option, "type_name", lang)} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params2) => <TextField {...params2} label={t("food_types", "Τύποι Φαγητού")} sx={{ mb: 2 }} />}
      />

      <Autocomplete
        multiple
        options={dietTypes}
        value={dietTypes.filter((x) => form.diet_type_ids.includes(x.id))}
        onChange={(_, vals) => setForm((p) => ({ ...p, diet_type_ids: vals.map((v) => v.id) }))}
        getOptionLabel={(o) => pickLocalized(o, "diet_name", lang)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip label={pickLocalized(option, "diet_name", lang)} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params2) => <TextField {...params2} label={t("diet_types", "Δίαιτες")} sx={{ mb: 2 }} />}
      />

      <Autocomplete
        multiple
        options={religiousPrefs}
        value={religiousPrefs.filter((x) => form.religious_preference_ids.includes(x.id))}
        onChange={(_, vals) => setForm((p) => ({ ...p, religious_preference_ids: vals.map((v) => v.id) }))}
        getOptionLabel={(o) => pickLocalized(o, "preference_name", lang)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip label={pickLocalized(option, "preference_name", lang)} {...getTagProps({ index })} />
          ))
        }
        renderInput={(params2) => <TextField {...params2} label={t("religious", "Θρησκευτικά")} sx={{ mb: 2 }} />}
      />

      <Divider sx={{ my: 3 }} />

      {/* PUBLISH / ROLE LOGIC */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("publish", "Δημοσίευση")}
      </Typography>

      {!isAdmin && !isPremium && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            "free_private_only",
            "Οι Free χρήστες μπορούν να κρατούν τις συνταγές τους μόνο ιδιωτικές. Για αίτημα δημοσίευσης απαιτείται Premium."
          )}
        </Alert>
      )}

      {isAdmin && (
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            label={t("visibility", "Ορατότητα")}
            value={form.visibility}
            onChange={handleChange("visibility")}
            fullWidth
          >
            <MenuItem value="private">{t("private", "Ιδιωτική")}</MenuItem>
            <MenuItem value="public">{t("public", "Δημόσια")}</MenuItem>
          </TextField>
        </Box>
      )}

      {isPremium && !isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            "premium_request_public_note",
            "Οι Premium χρήστες μπορούν να ζητήσουν δημοσίευση. Η συνταγή θα ελεγχθεί (anti-spam) και θα προστεθούν οι υπόλοιπες γλώσσες πριν γίνει δημόσια."
          )}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="contained" disabled={saving} onClick={handleSave}>
          {saving ? t("saving", "Αποθήκευση...") : t("save", "Αποθήκευση")}
        </Button>

        {isPremium && !isAdmin && (
          <Button variant="outlined" disabled={saving} onClick={handleRequestPublic}>
            {t("request_public", "Αίτημα για Δημόσια")}
          </Button>
        )}

        <Button variant="outlined" disabled={saving} onClick={() => navigate(`/${lang}/client/recipes-user`)}>
          {t("cancel", "Ακύρωση")}
        </Button>
      </Box>
    </Container>
  );
};

export default UpdateRecipeUser;
